import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { find, omit, isEmpty } from 'lodash';
import useLoadFilters from 'hooks/data/useLoadFilters';
import { hideLoader, showLoader } from 'actions/loader';
import withKeepingLastFocus from 'components/HOCs/withKeepingLastFocus';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import scaleSize from 'utils/scale-size';
import FilterPanel from './FilterPanel';
import ContentPanel from './ContentPanel';
import useContentNavigation from 'hooks/useContentNavigation';
import { useHistory } from 'react-router-dom';

const DEFAULT_SORT = 'uploadedOn,desc';
const LAST_FOCUS_KEY = 'FILTERS_PAGE';

const StyledFiltersPage = styled.div`
  position: relative;
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(1080)}px;
  left: ${scaleSize(100)}px;
`;

export const FiltersPage = withKeepingLastFocus(({ lastFocusKeeper }) => {
  const lastFocus = lastFocusKeeper.getSaved(LAST_FOCUS_KEY) || {};
  const history = useHistory();
  const dispatch = useDispatch();
  const { data: filters, isLoading: isFiltersLoading } = useLoadFilters();
  const [activeFilters, setActiveFilters] = useState(() => {
    return lastFocus.activeFilters ?? {};
  });
  const [isFiltersFolded, setIsFiltersFolded] = useState(false);

  const onContentEnter = useContentNavigation();

  const search = useMemo(() => {
    if (isEmpty(activeFilters)) return {};

    const result = {
      sort: DEFAULT_SORT,
    };
    Object.keys(activeFilters).forEach((key) => {
      const filter = filters[key];
      const activeOptions = activeFilters[key];

      result[filter.query] = activeOptions.map((option) => option.description).join(',');
    });

    return result;
  }, [activeFilters, filters]);

  const showMovies = !search.type || ['VOD_ASSET', 'ALL'].includes(search.type);
  const showSeries = !search.type || ['TV_SERIES', 'ALL'].includes(search.type);
  const clearSearch = omit(search, ['type']);

  const onItemFocus = useCallback(() => setIsFiltersFolded(true), []);
  const onItemEnter = useCallback(
    (item) => {
      const activeElementId = document.activeElement?.id;
      if (item.type === 'VIEW_ALL') {
        setActiveFilters((state) => {
          // Make item content type as selected filter
          const options = filters['CONTENT_TYPE_UI']?.values ?? [];
          const selected = find(options, ['description', item.contentType]);

          if (!selected) return state;

          return { ...state, CONTENT_TYPE_UI: [selected] };
        });
      } else if (item.type === 'FILTERS_BANNER') {
        history.push(`/list-grid/${item.id}`);
        lastFocusKeeper.save(LAST_FOCUS_KEY, { activeFilters, activeElementId });
      } else {
        onContentEnter(item);
        lastFocusKeeper.save(LAST_FOCUS_KEY, { activeFilters, activeElementId });
      }
    },
    [activeFilters, filters, history, lastFocusKeeper, onContentEnter],
  );

  const onFilterFocus = useCallback(() => setIsFiltersFolded(false), []);

  useEffect(() => {
    dispatch(isFiltersLoading ? showLoader() : hideLoader());
  }, [dispatch, isFiltersLoading]);

  // Clear last focus after "mount"
  useEffect(() => {
    if (!lastFocusKeeper.hasSaved(LAST_FOCUS_KEY)) return;

    const { activeElementId } = lastFocusKeeper.getSaved(LAST_FOCUS_KEY) ?? {};

    if (activeElementId) SpatialNavigationWrapper.focus(`#${activeElementId}`);
    // async timeout necessary here
    setTimeout(() => lastFocusKeeper.reset(LAST_FOCUS_KEY), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledFiltersPage>
      {filters && (
        <FilterPanel
          isFolded={isFiltersFolded}
          filters={filters}
          activeFilters={activeFilters}
          setActive={setActiveFilters}
          onFocus={onFilterFocus}
        />
      )}
      {filters && (
        <ContentPanel
          isFiltersFolded={isFiltersFolded}
          showMovies={showMovies}
          showSeries={showSeries}
          search={clearSearch}
          onItemEnter={onItemEnter}
          onItemFocus={onItemFocus}
        />
      )}
    </StyledFiltersPage>
  );
});
